import { request } from "../base";
import type { Pro, TeamMember } from "../pro";
import type { ProStats } from "../pro/analytics";
import type {
	CompanyProfile,
	ProProfile,
	ProLeadership,
	ProCertification,
	ProTestimonial,
	UpdateCompanyProfileInput,
	AddLeadershipInput,
	UpdateLeadershipInput,
	AddCertificationInput,
	UpdateCertificationInput,
	AddTestimonialInput,
	UpdateTestimonialInput,
} from "../company-profile";

export const adminProsApi = {
	// Pro CRUD
	async listPros(params?: Record<string, string>) {
		const query = params ? `?${new URLSearchParams(params).toString()}` : "";
		return request<Pro[]>(`/api/admin/pros${query}`);
	},

	async getPro(id: string) {
		return request<Pro>(`/api/admin/pros/${id}`);
	},

	async createPro(data: Partial<Pro>) {
		return request<Pro>(`/api/admin/pros`, {
			method: "POST",
			body: data,
		});
	},

	async updatePro(id: string, data: Partial<Pro>) {
		return request<Pro>(`/api/admin/pros/${id}`, {
			method: "PUT",
			body: data,
		});
	},

	async deletePro(id: string) {
		return request(`/api/admin/pros/${id}`, {
			method: "DELETE",
		});
	},

	async enablePro(id: string) {
		return request<Pro>(`/api/admin/pros/${id}/enable`, {
			method: "POST",
		});
	},

	async disablePro(id: string) {
		return request<Pro>(`/api/admin/pros/${id}/disable`, {
			method: "POST",
		});
	},

	async setProFeatured(id: string, isFeatured: boolean) {
		return request<Pro>(`/api/admin/pros/${id}/featured`, {
			method: "POST",
			body: { isFeatured },
		});
	},

	// Pro Team Management
	async getProTeam(proId: string) {
		return request<TeamMember[]>(`/api/admin/pros/${proId}/team`);
	},

	async addProTeamMember(
		proId: string,
		data: { userId: string; role: "owner" | "manager" | "staff" },
	) {
		return request<TeamMember>(`/api/admin/pros/${proId}/team`, {
			method: "POST",
			body: data,
		});
	},

	async updateProTeamMember(
		proId: string,
		roleId: number,
		data: { role: "owner" | "manager" | "staff" },
	) {
		return request<TeamMember>(
			`/api/admin/pros/${proId}/team/${roleId}`,
			{
				method: "PUT",
				body: data,
			},
		);
	},

	async removeProTeamMember(proId: string, roleId: number) {
		return request(`/api/admin/pros/${proId}/team/${roleId}`, {
			method: "DELETE",
		});
	},

	// Pro Analytics
	async getProStats(proId: string) {
		return request<ProStats>(`/api/stats/pro/${proId}`);
	},

	// Company Profile Management
	async getCompanyProfile(proId: string) {
		return request<CompanyProfile>(
			`/api/admin/pros/${proId}/company-profile`,
		);
	},

	async updateCompanyProfile(
		proId: string,
		data: UpdateCompanyProfileInput,
	) {
		return request<ProProfile>(
			`/api/admin/pros/${proId}/company-profile`,
			{
				method: "PUT",
				body: data,
			},
		);
	},

	// Leadership Team
	async addLeadershipMember(proId: string, data: AddLeadershipInput) {
		return request<ProLeadership>(
			`/api/admin/pros/${proId}/company-profile/leadership`,
			{
				method: "POST",
				body: data,
			},
		);
	},

	async updateLeadershipMember(
		proId: string,
		memberId: number,
		data: UpdateLeadershipInput,
	) {
		return request<ProLeadership>(
			`/api/admin/pros/${proId}/company-profile/leadership/${memberId}`,
			{
				method: "PUT",
				body: data,
			},
		);
	},

	async deleteLeadershipMember(proId: string, memberId: number) {
		return request(
			`/api/admin/pros/${proId}/company-profile/leadership/${memberId}`,
			{
				method: "DELETE",
			},
		);
	},

	// Certifications
	async addCertification(proId: string, data: AddCertificationInput) {
		return request<ProCertification>(
			`/api/admin/pros/${proId}/company-profile/certifications`,
			{
				method: "POST",
				body: data,
			},
		);
	},

	async updateCertification(
		proId: string,
		certId: number,
		data: UpdateCertificationInput,
	) {
		return request<ProCertification>(
			`/api/admin/pros/${proId}/company-profile/certifications/${certId}`,
			{
				method: "PUT",
				body: data,
			},
		);
	},

	async deleteCertification(proId: string, certId: number) {
		return request(
			`/api/admin/pros/${proId}/company-profile/certifications/${certId}`,
			{
				method: "DELETE",
			},
		);
	},

	// Testimonials
	async addTestimonial(proId: string, data: AddTestimonialInput) {
		return request<ProTestimonial>(
			`/api/admin/pros/${proId}/company-profile/testimonials`,
			{
				method: "POST",
				body: data,
			},
		);
	},

	async updateTestimonial(
		proId: string,
		testimonialId: number,
		data: UpdateTestimonialInput,
	) {
		return request<ProTestimonial>(
			`/api/admin/pros/${proId}/company-profile/testimonials/${testimonialId}`,
			{
				method: "PUT",
				body: data,
			},
		);
	},

	async deleteTestimonial(proId: string, testimonialId: number) {
		return request(
			`/api/admin/pros/${proId}/company-profile/testimonials/${testimonialId}`,
			{
				method: "DELETE",
			},
		);
	},
};
